/**
 * Core types and enums for rendering system
 */

export enum PlaceState {
  Empty = "empty",
  Occupied = "occupied",
  Blocked = "blocked",
}

export enum LOD {
  HIGH = 2,
  MEDIUM = 1,
  LOW = 0,
}

export enum DirtyFlag {
  DATA = "data", // Places data changed
  THEME = "theme", // Theme changed
  VIEWPORT = "viewport", // View moved/scaled
  SELECTION = "selection", // Selection changed
  CONFIG = "config", // Configuration changed
}

export interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface Size {
  width: number;
  height: number;
  bufferOffset?: number; // Offset for BufferedCanvasCache coordinates
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Transform function that converts coordinates from one space to another
 * @example worldToScreen, screenToWorld
 */
export type TransformFunction = (x: number, y: number) => Point;

/**
 * Configuration for BufferedCanvasCache
 */
export interface BufferedCacheConfig {
  /**
   * Size of buffer around visible area (in pixels)
   * Larger values = smoother panning but more memory
   * @default 500
   */
  bufferSize?: number;

  /**
   * Distance threshold for re-rendering (in pixels)
   * When panning exceeds this, cache is invalidated
   * @default 200
   */
  panThreshold?: number;
}

export interface ColorSet {
  face: string;
  top: string;
  right: string;
  stroke: string;
}

export interface CachedDimensions {
  placeWidth: number;
  placeHeight: number;
  placeSpacing: number;
  isoDepthX: number;
  isoDepthY: number;
}

export interface PlaceBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  isoDepthX: number;
  isoDepthY: number;
}

export const RENDER_CONSTANTS = {
  FACE_BRIGHTNESS: -5,
  RIGHT_FACE_BRIGHTNESS: -10,
  STROKE_BRIGHTNESS: -25,
  CULLING_BUFFER: 50,
  CACHE_MAX_SIZE: 100,
  PATH_CACHE_MAX_SIZE: 50,
  OFFSCREEN_CACHE_MAX_SIZE: 100,
  SCALE_QUANTIZE_DECIMALS: 2,
} as const;

/**
 * Column structure for grouping places with same baseX/baseY
 */
export interface PlaceColumn {
  places: any[]; // Sparse array where index = level
  baseX: number;
  baseY: number;
  maxLevel: number;
  depth: number; // Calculated depth for painter's algorithm
}

/**
 * Configuration for PlaceRenderer
 */
export interface PlaceRendererConfig {
  selectionLineWidth: number;
  borderLineWidth: number;
  depthFactorPerLevel: number;
  isoDepthFactorX: number;
  isoDepthFactorY: number;
  spacingFactor: number;
  labelFont: (scale: number) => string;
  labelMinScale: number;
  labelMinTextSize: number;
  addressLabelFont: (scale: number) => string;
  addressLabelMinScale: number;
  addressLabelOffset: number;
}
